import type { PropsElement } from '../props';
import { materialShare } from '../props';
import type { ToolFn, ToolSpec } from '../types';
import { compare, fieldValue, type Op } from './fields';
import { inStorey, ofType, round } from './select';
import { materialMatcher } from './vocabulary';

export const querySpec: ToolSpec = {
  name: 'query_elements',
  description:
    'General query over the elements: filter by type, storey, material and any number of `where` conditions on a field, then sort, group and aggregate. Fields: type, name, storey, material, volume (m³), area (m²), q:<QuantityName> (e.g. q:NetVolume, q:Width, q:Height), p:<PropertySet>.<Property> (e.g. p:Pset_WallCommon.FireRating). Use describe_model to learn which exist. Examples: the biggest room → type=IfcSpace, sort_by=area, order=desc, limit=1; volume of walls per storey → type=IfcWall, aggregate=sum, field=volume, group_by=storey; walls thicker than 30 cm → type=IfcWall, where=[{field:"q:Width", op:"gt", value:0.3}].',
  input_schema: {
    type: 'object',
    properties: {
      type: { type: 'string' },
      storey: { type: 'string' },
      material: { type: 'string', description: 'class (concrete, steel, timber, …) or substring' },
      name_contains: { type: 'string' },
      where: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            field: { type: 'string' },
            op: {
              type: 'string',
              enum: ['eq', 'ne', 'contains', 'gt', 'lt', 'gte', 'lte', 'missing', 'set'],
            },
            value: {},
          },
          required: ['field', 'op'],
        },
      },
      aggregate: { type: 'string', enum: ['count', 'sum', 'avg', 'min', 'max', 'list'] },
      field: { type: 'string', description: 'numeric field for sum/avg/min/max, default volume' },
      group_by: { type: 'string', description: 'type | storey | material | any field' },
      sort_by: { type: 'string' },
      order: { type: 'string', enum: ['asc', 'desc'] },
      limit: { type: 'integer' },
    },
  },
};

interface Where {
  field: string;
  op: Op;
  value?: unknown;
}

export const query =
  (elements: PropsElement[]): ToolFn =>
  (input) => {
    const { type, storey, material, name_contains, aggregate, group_by, sort_by, order } = input;
    let hits = inStorey(ofType(elements, type), storey);
    const matcher = materialMatcher(material);
    if (matcher) hits = hits.filter((e) => materialShare(e.material, matcher.test) > 0);
    if (name_contains)
      hits = hits.filter((e) => e.name.toLowerCase().includes(String(name_contains).toLowerCase()));
    const where = Array.isArray(input.where) ? (input.where as Where[]) : [];
    for (const w of where) {
      if (!w || typeof w.field !== 'string') continue;
      hits = hits.filter((e) => compare(fieldValue(e, w.field), w.op ?? 'set', w.value));
    }
    const field = String(input.field ?? 'volume');
    const agg = String(aggregate ?? (group_by ? 'count' : 'list'));

    if (sort_by) {
      const key = String(sort_by);
      const dir = order === 'asc' ? 1 : -1;
      hits = [...hits].sort((a, b) => {
        const va = fieldValue(a, key);
        const vb = fieldValue(b, key);
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        return typeof va === 'number' && typeof vb === 'number'
          ? (va - vb) * dir
          : String(va).localeCompare(String(vb)) * dir;
      });
    }

    const limit = Math.min(Number(input.limit) || (agg === 'list' ? 20 : 0), 60);
    const fields = new Set<string>(['volume']);
    if (sort_by) fields.add(String(sort_by));
    if (agg !== 'count' && agg !== 'list') fields.add(field);
    for (const w of where) if (w?.field) fields.add(w.field);

    const result: Record<string, unknown> = {
      count: hits.length,
      ids: hits.map((e) => e.expressID),
    };
    if (agg !== 'list' && agg !== 'count') {
      const stat = aggregateOf(hits, field, agg);
      result.aggregate = {
        op: agg,
        field,
        value: stat.value,
        elements_with_value: stat.n,
        missing: hits.length - stat.n,
      };
    }
    if (group_by) {
      const key = String(group_by);
      const groups = new Map<string, PropsElement[]>();
      for (const e of hits) {
        const g = String(fieldValue(e, key) ?? '–');
        groups.set(g, [...(groups.get(g) ?? []), e]);
      }
      const rows = [...groups.entries()].map(([g, els]) => {
        const stat =
          agg === 'count' || agg === 'list'
            ? { value: els.length, n: els.length }
            : aggregateOf(els, field, agg);
        return [g, stat.value == null ? null : round(stat.value, 2)] as const;
      });
      rows.sort((a, b) => (b[1] ?? -Infinity) - (a[1] ?? -Infinity));
      result.groups = Object.fromEntries(rows.slice(0, 40));
      result.group_by = key;
      result.group_op = agg === 'list' ? 'count' : agg;
    }
    if (limit > 0)
      result.elements = hits.slice(0, limit).map((e) => ({
        id: e.expressID,
        type: e.type,
        name: e.name.slice(0, 60),
        storey: e.storey,
        ...(e.material ? { material: e.material.names.join(' | ') } : {}),
        ...Object.fromEntries([...fields].map((f) => [f, roundish(fieldValue(e, f))])),
      }));
    return result;
  };

function aggregateOf(
  els: PropsElement[],
  field: string,
  op: string,
): { value: number | null; n: number } {
  const values = els
    .map((e) => fieldValue(e, field))
    .filter((v): v is number => typeof v === 'number');
  const n = values.length;
  if (n === 0) return { value: null, n: 0 };
  let value: number;
  switch (op) {
    case 'sum':
      value = values.reduce((a, b) => a + b, 0);
      break;
    case 'avg':
      value = values.reduce((a, b) => a + b, 0) / n;
      break;
    case 'min':
      value = Math.min(...values);
      break;
    case 'max':
      value = Math.max(...values);
      break;
    default:
      value = n;
  }
  return { value: round(value, 3), n };
}

const roundish = (v: string | number | null) => (typeof v === 'number' ? round(v, 3) : v);
